import { Camera, CameraInput, FastPlayback, FrameCapture, FrameCaptures, Position } from '../../api/camera';

export type FastPlaybackForm = {
  speed: string;
  bandwidth: string;
  framerate: string;
  storage: string;
  time: string;
};

export type FrameCaptureForm = {
  cron: string;
  size: string;
  priority: string;
  ref: string;
  wait: string;
  pan: string;
  tilt: string;
  zoom: string;
};

export type CameraForm = {
  id: string;
  name: string;
  client: string;
  controlTime: number;
  active: boolean;
  keepConnected: boolean;
  latitude: string;
  longitude: string;
  timezone: string;
  model: string;
  settings: any;
  server: string;
  record: {
    enabled: boolean;
    storage: number;
    time: number;
  };
  fastPlaybacks: Array<FastPlaybackForm>;
  alert: {
    enabled: boolean;
    interval: number;
  };
  emails: Array<string>;
  frameCaptures: Array<FrameCaptureForm>;
  allowedDomains: Array<string>;
};

export const initialState: CameraForm = {
  id: '',
  name: '',
  client: '1',
  controlTime: 20,
  active: true,
  keepConnected: true,
  latitude: '',
  longitude: '',
  timezone: 'America/Los_Angeles',
  model: 'generic-rtsp',
  settings: {},
  server: '32',
  record: {
    enabled: false,
    storage: 100,
    time: 0,
  },
  fastPlaybacks: [],
  alert: {
    enabled: false,
    interval: 5,
  },
  emails: [],
  frameCaptures: [],
  allowedDomains: [],
};

export function toForm(camera: Camera): CameraForm {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { panoramas, frameCaptures, ...form } = camera;
  const _frameCaptures: Array<FrameCaptureForm> = (frameCaptures || [])
    .map((frameCapture) => {
      return frameCapture.captures.map((capture) => {
        return {
          cron: frameCapture.cron,
          size: frameCapture.size,
          priority: frameCapture.priority.toString(),
          ref: capture.ref,
          wait: capture.wait.toString(),
          pan: capture.position?.pan?.toString() || '',
          tilt: capture.position?.tilt?.toString() || '',
          zoom: capture.position?.zoom?.toString() || '',
        };
      });
    })
    .flat();
  const fastPlaybacks: Array<FastPlaybackForm> =
    camera.record?.fastPlaybacks?.map((fastPlayback) => {
      return {
        speed: fastPlayback.speed?.toString() || '',
        bandwidth: fastPlayback.bandwidth?.toString() || '',
        framerate: fastPlayback.framerate?.toString() || '',
        storage: fastPlayback.storage?.toString() || '',
        time: fastPlayback.time?.toString() || '',
      };
    }) || initialState.fastPlaybacks;
  const record = camera.record ? { ...camera.record } : initialState.record;
  const emails: Array<string> = [...camera.alert?.emails] || [];
  const allowedDomains = [...camera.allowedDomains];
  const latitude = camera.latitude?.toString() || '';
  const longitude = camera.longitude?.toString() || '';
  const result = {
    ...form,
    frameCaptures: _frameCaptures,
    record,
    emails,
    allowedDomains,
    latitude,
    longitude,
    fastPlaybacks,
    client: camera.client.id,
    server: camera.server.id,
  };
  return result;
}

export function toCamera(form: CameraForm): CameraInput {
  const { fastPlaybacks, frameCaptures, emails, ...input } = form;
  const _frameCaptures: Array<FrameCaptures> = [];
  frameCaptures.sort((o1, o2) => {
    if (o1.priority > o2.priority) return 1;
    if (o1.priority < o2.priority) return -1;
    if (o1.cron > o2.cron) return 1;
    if (o1.cron < o2.cron) return -1;
    if (o1.size > o2.size) return 1;
    if (o1.size < o2.size) return -1;
    return 0;
  });
  let previous: FrameCaptureForm | null = null;
  let item: FrameCaptures | null = null;
  frameCaptures
    .filter((entry: FrameCaptureForm) => !!entry.ref)
    .forEach((entry: FrameCaptureForm) => {
      if (
        previous == null ||
        entry.priority != previous.priority ||
        entry.cron != previous.cron ||
        entry.size != previous.size
      ) {
        if (!!entry.cron && !!entry.size) {
          item = {
            id: Math.floor(Math.random() * 100000).toString(),
            cron: entry.cron,
            size: entry.size,
            priority: parseInt(entry.priority) || 0,
            captures: [],
          };
          _frameCaptures.push(item);
        } else item = null;
      }
      if (item && !!entry.ref) {
        const hasPosition = !!entry.pan && !!entry.tilt && !!entry.zoom;
        const position: Position | undefined = hasPosition
          ? {
              pan: parseFloat(entry.pan) || 0,
              tilt: parseFloat(entry.tilt) || 0,
              zoom: parseFloat(entry.zoom) || 0,
            }
          : undefined;
        const wait: number = parseFloat(entry.wait) || 0;
        const capture: FrameCapture = { ref: entry.ref, position, wait };
        if (item) item.captures.push(capture);
      }
      previous = entry;
    });
  const _fastPlaybacks: Array<FastPlayback> = fastPlaybacks
    .filter((fastPlayback: FastPlaybackForm): boolean => !!fastPlayback.speed)
    .map((fastPlayback: FastPlaybackForm): FastPlayback => {
      return {
        speed: parseInt(fastPlayback.speed) || 0,
        bandwidth: parseInt(fastPlayback.bandwidth) || 0,
        framerate: parseInt(fastPlayback.framerate) || 0,
        storage: parseInt(fastPlayback.storage) || 0,
        time: parseInt(fastPlayback.time) || 0,
      };
    });
  return {
    ...input,
    record: { ...form.record, fastPlaybacks: _fastPlaybacks },
    alert: { ...form.alert, emails: emails.filter((email: string): boolean => !!email) },
    frameCaptures: _frameCaptures,
    allowedDomains: form.allowedDomains.filter((allowedDomain: string): boolean => !!allowedDomain),
    latitude: parseFloat(form.latitude) || 0,
    longitude: parseFloat(form.longitude) || 0,
  };
}
